import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import PageSidebarContent from './PageSidebarContent';
import UserSettingsModal from './UserSettingsModal';
import { useSidebarContext } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import EmailVerificationBanner from '../features/emailVerification/components/EmailVerificationBanner';
import BillingAccessNotices from './billing/BillingAccessNotices';
import '../features/emailVerification/EmailVerification.css';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  isEditMode?: boolean; // 編集モードフラグを追加
  isTitleEditing?: boolean; // タイトル編集中フラグを追加
  // onUndo?: () => void; // Undo機能のコールバック - 一時的に無効化（後日実装予定）
  // onRedo?: () => void; // Redo機能のコールバック - 一時的に無効化（後日実装予定）
  onInsertImage?: () => void; // 画像挿入機能のコールバック
  onBeforeHomeNavigation?: () => Promise<void>; // ホーム遷移前の保存処理
  onHomeClick?: () => void; // ワークスペースホームクリック時の追加処理
  hideHeader?: boolean; // 埋め込み等でヘッダー/サイドバーを非表示
  sidebarContent?: React.ReactNode; // 表示画面ごとに差し替えるサイドバー内容
  isSidebarDisabled?: boolean; // サイドバーを強制的に閉じてトグルを無効化
  showHeaderSearchInput?: boolean; // ヘッダー中央の検索入力を表示するか
}

const Layout: React.FC<LayoutProps> = ({
  children,
  isEditMode = false,
  isTitleEditing = false,
  onInsertImage,
  onBeforeHomeNavigation,
  onHomeClick,
  hideHeader = false,
  sidebarContent,
  isSidebarDisabled = false,
  showHeaderSearchInput = false,
}) => {
  const { workspaceId } = useParams();
  const { isOpen, isMobile, closeSidebar, syncSidebarForWorkspace } = useSidebarContext();
  const { user, initializing } = useAuth();
  const navigate = useNavigate();
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);
  const isSidebarVisible = !hideHeader && !isSidebarDisabled && isOpen;

  // 未ログインの場合はログイン画面にリダイレクト（ローディング中は除外）
  useEffect(() => {
    if (!initializing && !user) {
      navigate('/sys/login', { replace: true });
    }
  }, [user, initializing, navigate]);

  useEffect(() => {
    if (hideHeader) {
      return;
    }

    syncSidebarForWorkspace(workspaceId, { forceClosed: isSidebarDisabled });
  }, [hideHeader, isSidebarDisabled, syncSidebarForWorkspace, workspaceId]);

  // スマホでサイドバーが開いている時にメインコンテンツをクリックした場合の処理
  const handleMainClick = () => {
    if (isMobile && isOpen) {
      closeSidebar();
    }
  };

  return (
    <div>
      {!hideHeader && (
        <Header 
          isEditMode={isEditMode} 
          isTitleEditing={isTitleEditing} 
          onInsertImage={onInsertImage} 
          onBeforeHomeNavigation={onBeforeHomeNavigation}
          onHomeClick={onHomeClick}
          onUserSettingsOpen={() => setUserSettingsOpen(true)}
          isSidebarToggleDisabled={isSidebarDisabled}
          showSearchInput={showHeaderSearchInput}
        />
      )}
      {!hideHeader && <EmailVerificationBanner />}
      {!hideHeader && <BillingAccessNotices />}
      {!hideHeader && (
        <Sidebar forcedClosed={isSidebarDisabled}>
          {sidebarContent ?? <PageSidebarContent />}
        </Sidebar>
      )}
      <main
        className={`layout-main ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}
        onClick={handleMainClick}
      >
        {children}
      </main>
      
      {/* ユーザー設定モーダル */}
      {!hideHeader && (
        <UserSettingsModal
          isOpen={userSettingsOpen}
          onClose={() => setUserSettingsOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 